import { useEffect, useRef, useState } from "react";
import { IOnCallAgent, IOnCallUserOption } from "../models/IOnCallAgent";
import {
  addOnCallAgent,
  deleteOnCallAgent,
  getOnCallAgents,
  getOnCallUserList,
  patchOnCallAgent,
} from "../services/onCallAgentService";

const EMPTY_FORM = { displayName: "", phoneNumber: "", userId: "", isActive: true };

function getInitials(name: string) {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("tr-TR", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

/* ── Toggle switch ── */
function Toggle({ on, loading, onChange }: { on: boolean; loading: boolean; onChange: () => void }) {
  return (
    <button
      onClick={onChange}
      disabled={loading}
      title={on ? "Pasif yap" : "Aktif yap"}
      style={{
        width: 40, height: 22, borderRadius: 11, border: "none",
        background: on ? "#22c55e" : "#d1d5db",
        position: "relative", cursor: loading ? "default" : "pointer",
        flexShrink: 0, transition: "background 0.2s",
        opacity: loading ? 0.6 : 1,
      }}
    >
      <span style={{
        position: "absolute", top: 3, left: on ? 21 : 3,
        width: 16, height: 16, borderRadius: "50%", background: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        transition: "left 0.2s",
      }} />
    </button>
  );
}

/* ── Agent kartı ── */
function AgentCard({
  agent, onToggle, onDelete, toggleLoading, deleteLoading,
}: {
  agent: IOnCallAgent;
  onToggle: () => void;
  onDelete: () => void;
  toggleLoading: boolean;
  deleteLoading: boolean;
}) {
  const [confirmDel, setConfirmDel] = useState(false);
  const initials = getInitials(agent.displayName);

  return (
    <div style={{
      border: "1.5px solid #e5e7eb", borderRadius: 14,
      background: agent.isActive ? "#fff" : "#f9fafb",
      overflow: "hidden",
      transition: "border-color 0.15s",
    }}>
      <div style={{ padding: "12px 14px", display: "flex", alignItems: "center", gap: 12 }}>
        {/* Avatar */}
        <div style={{
          width: 42, height: 42, borderRadius: 12, flexShrink: 0,
          background: agent.isActive
            ? "linear-gradient(135deg, #22c55e, #16a34a)"
            : "linear-gradient(135deg, #9ca3af, #6b7280)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 800, fontSize: "0.88rem", color: "#fff",
          boxShadow: agent.isActive ? "0 2px 8px rgba(34,197,94,0.3)" : "none",
        }}>
          {initials}
        </div>

        {/* Bilgi */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#111827", display: "flex", alignItems: "center", gap: 6 }}>
            {agent.displayName}
            <span style={{
              fontSize: "0.6rem", fontWeight: 700,
              background: agent.isActive ? "#dcfce7" : "#f3f4f6",
              color: agent.isActive ? "#16a34a" : "#9ca3af",
              border: `1px solid ${agent.isActive ? "#bbf7d0" : "#e5e7eb"}`,
              borderRadius: 6, padding: "1px 6px", textTransform: "uppercase", letterSpacing: "0.04em",
            }}>
              {agent.isActive ? "Aktif" : "Pasif"}
            </span>
          </div>
          <div style={{ fontSize: "0.76rem", color: "#6b7280", marginTop: 1, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span><i className="bi bi-telephone me-1" />{agent.phoneNumber || "—"}</span>
            <span style={{ color: "#d1d5db" }}>·</span>
            <span style={{ fontSize: "0.66rem", color: "#9ca3af" }}>{formatDate(agent.createdAt)}</span>
          </div>
        </div>

        {/* Aksiyonlar */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <Toggle on={agent.isActive} loading={toggleLoading} onChange={onToggle} />

          {confirmDel ? (
            <div style={{ display: "flex", gap: 4 }}>
              <button
                onClick={() => { onDelete(); setConfirmDel(false); }}
                disabled={deleteLoading}
                style={{
                  background: "#dc2626", border: "none", borderRadius: 7,
                  padding: "4px 10px", fontWeight: 700, fontSize: "0.72rem",
                  color: "#fff", cursor: "pointer",
                }}
              >
                {deleteLoading ? <span className="spinner-border spinner-border-sm" /> : "Sil"}
              </button>
              <button
                onClick={() => setConfirmDel(false)}
                style={{
                  background: "#f1f5f9", border: "1px solid #e5e7eb", borderRadius: 7,
                  padding: "4px 8px", fontWeight: 600, fontSize: "0.72rem",
                  color: "#6b7280", cursor: "pointer",
                }}
              >
                İptal
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmDel(true)}
              title="Kaldır"
              style={{
                background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8,
                width: 32, height: 32,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", color: "#dc2626", fontSize: "0.82rem",
              }}
            >
              <i className="bi bi-trash3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Kullanıcı seçici ── */
function UserPicker({
  onSelect,
  onClose,
}: {
  onSelect: (u: IOnCallUserOption) => void;
  onClose: () => void;
}) {
  const [users, setUsers] = useState<IOnCallUserOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getOnCallUserList()
      .then(setUsers)
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const filtered = users.filter((u) =>
    `${u.displayName} ${u.phoneNumber}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{
      position: "absolute", inset: 0, zIndex: 10,
      background: "#fff", display: "flex", flexDirection: "column",
    }}>
      {/* Picker header */}
      <div style={{
        padding: "12px 16px", borderBottom: "1.5px solid #f1f5f9",
        display: "flex", alignItems: "center", gap: 10, flexShrink: 0,
        background: "#fafafa",
      }}>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", fontSize: "1rem", padding: 0 }}>
          <i className="bi bi-arrow-left" />
        </button>
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Kullanıcı ara…"
          style={{
            flex: 1, borderRadius: 9, border: "1.5px solid #e0e7ff",
            padding: "7px 12px", fontSize: "0.88rem", outline: "none",
            background: "#f8faff", fontFamily: "inherit",
          }}
        />
      </div>

      {/* User list */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {loading && (
          <div style={{ display: "flex", justifyContent: "center", padding: "32px 0" }}>
            <span className="spinner-border text-primary" />
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: "32px 16px", color: "#9ca3af", fontSize: "0.84rem" }}>
            Kullanıcı bulunamadı
          </div>
        )}
        {filtered.map((u) => (
          <button
            key={u.id}
            onClick={() => onSelect(u)}
            style={{
              width: "100%", textAlign: "left", border: "none",
              borderBottom: "1px solid #f1f5f9", background: "transparent",
              padding: "12px 16px", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 10,
            }}
          >
            <div style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              background: "linear-gradient(135deg, #6366f1, #4f46e5)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 800, fontSize: "0.78rem", color: "#fff",
            }}>
              {getInitials(u.displayName)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: "0.86rem", color: "#111827" }}>{u.displayName}</div>
              {u.phoneNumber && (
                <div style={{ fontSize: "0.72rem", color: "#9ca3af", marginTop: 1 }}>{u.phoneNumber}</div>
              )}
            </div>
            <i className="bi bi-chevron-right" style={{ color: "#d1d5db", fontSize: "0.7rem" }} />
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── Ana modal ── */
export function OnCallAgentModal({
  onClose,
  myUserId,
}: {
  onClose: () => void;
  myUserId: string | null;
}) {
  const [agents, setAgents] = useState<IOnCallAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const [showPicker, setShowPicker] = useState(false);
  const [toggleLoading, setToggleLoading] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

  const [isNarrow, setIsNarrow] = useState(window.innerWidth < 640);

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const loadAgents = () => {
    setLoading(true);
    setLoadError(null);
    getOnCallAgents()
      .then((res) => setAgents(res.data))
      .catch(() => setLoadError("Liste yüklenemedi."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadAgents(); }, []);

  const handleToggle = async (agent: IOnCallAgent) => {
    setToggleLoading(agent.id);
    try {
      await patchOnCallAgent(agent.id, !agent.isActive);
      await loadAgents();
    } finally {
      setToggleLoading(null);
    }
  };

  const handleDelete = async (id: string) => {
    setDeleteLoading(id);
    try {
      await deleteOnCallAgent(id);
      setAgents((prev) => prev.filter((a) => a.id !== id));
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleAddSelf = () => {
    setForm((prev) => ({ ...prev, userId: myUserId ?? "" }));
    setShowAdd(true);
  };

  const handleUserPicked = (u: IOnCallUserOption) => {
    setForm((prev) => ({
      ...prev,
      userId: u.id,
      displayName: prev.displayName || u.displayName,
      phoneNumber: prev.phoneNumber || u.phoneNumber,
    }));
    setShowPicker(false);
  };

  const handleAddSubmit = async () => {
    if (!form.displayName.trim() || !form.userId.trim()) {
      setAddError("Ad ve kullanıcı alanları zorunludur.");
      return;
    }
    setAddLoading(true);
    setAddError(null);
    try {
      await addOnCallAgent({
        displayName: form.displayName.trim(),
        phoneNumber: form.phoneNumber.trim().replace(/^0+/, ""),
        userId: form.userId.trim(),
        isActive: form.isActive,
      });
      setForm(EMPTY_FORM);
      setShowAdd(false);
      await loadAgents();
    } catch {
      setAddError("Nöbetçi eklenemedi. Lütfen tekrar deneyin.");
    } finally {
      setAddLoading(false);
    }
  };

  const panelStyle: React.CSSProperties = isNarrow
    ? { position: "fixed", inset: 0, zIndex: 2050, background: "#fff", display: "flex", flexDirection: "column", overflow: "hidden" }
    : {
        position: "fixed", top: "50%", left: "50%", zIndex: 2050,
        transform: "translate(-50%, -50%)",
        width: "min(580px, calc(100vw - 24px))",
        maxHeight: "88dvh",
        background: "#fff", borderRadius: 20,
        boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
        display: "flex", flexDirection: "column", overflow: "hidden",
      };

  const activeCount = agents.filter((a) => a.isActive).length;

  return (
    <>
      {!isNarrow && (
        <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 2040, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }} />
      )}

      <div style={panelStyle}>
        {/* Header */}
        <div style={{
          padding: isNarrow ? "14px 16px" : "16px 20px",
          background: "linear-gradient(135deg, #064e3b, #065f46)",
          display: "flex", alignItems: "center", gap: 12,
          flexShrink: 0,
        }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0, background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className="bi bi-telephone-fill text-white" style={{ fontSize: "1rem" }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: "0.96rem", color: "#fff" }}>Nöbetçiler</div>
            <div style={{ fontSize: "0.66rem", color: "rgba(255,255,255,0.65)" }}>
              {loading ? "Yükleniyor…" : `${agents.length} kayıt · ${activeCount} aktif`}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 9, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", fontSize: "0.9rem" }}>
            <i className="bi bi-x-lg" />
          </button>
        </div>

        {/* Scrollable body — with relative positioning for user picker overlay */}
        <div style={{ flex: 1, overflowY: "auto", position: "relative" }}>
          {/* User picker overlay */}
          {showPicker && (
            <UserPicker onSelect={handleUserPicked} onClose={() => setShowPicker(false)} />
          )}

          <div style={{ padding: isNarrow ? "14px" : "18px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
            {/* Loading */}
            {loading && (
              <div style={{ display: "flex", justifyContent: "center", padding: "32px 0" }}>
                <span className="spinner-border text-success" />
              </div>
            )}

            {/* Hata */}
            {loadError && (
              <div style={{ padding: "10px 14px", borderRadius: 10, background: "#fef2f2", border: "1.5px solid #fecaca", color: "#991b1b", fontSize: "0.82rem", display: "flex", alignItems: "center", gap: 8 }}>
                <i className="bi bi-exclamation-circle-fill" />
                {loadError}
                <button onClick={loadAgents} style={{ marginLeft: "auto", background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontWeight: 700, fontSize: "0.78rem" }}>Tekrar dene</button>
              </div>
            )}

            {/* Boş liste */}
            {!loading && !loadError && agents.length === 0 && (
              <div style={{ textAlign: "center", padding: "28px 0", color: "#9ca3af", fontSize: "0.84rem" }}>
                <i className="bi bi-moon-stars" style={{ fontSize: "2rem", display: "block", marginBottom: 8, color: "#d1d5db" }} />
                Henüz nöbetçi eklenmemiş
              </div>
            )}

            {/* Agent kartları */}
            {agents.map((a) => (
              <AgentCard
                key={a.id}
                agent={a}
                toggleLoading={toggleLoading === a.id}
                deleteLoading={deleteLoading === a.id}
                onToggle={() => handleToggle(a)}
                onDelete={() => handleDelete(a.id)}
              />
            ))}

            {/* Ayırıcı + Ekle başlığı */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
              <div style={{ flex: 1, height: 1, background: "#f1f5f9" }} />
              <span style={{ fontSize: "0.68rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em" }}>Nöbetçi Ekle</span>
              <div style={{ flex: 1, height: 1, background: "#f1f5f9" }} />
            </div>

            {/* Hızlı ekle butonları */}
            {!showAdd && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  onClick={handleAddSelf}
                  style={{
                    flex: "1 1 160px", background: "linear-gradient(135deg, #6366f1, #4f46e5)",
                    border: "none", borderRadius: 10, padding: "10px 14px",
                    fontWeight: 700, fontSize: "0.84rem", color: "#fff",
                    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                    boxShadow: "0 2px 10px rgba(99,102,241,0.3)",
                  }}
                >
                  <i className="bi bi-person-check-fill" /> Kendimi Ekle
                </button>
                <button
                  onClick={() => setShowAdd(true)}
                  style={{
                    flex: "1 1 140px", background: "#fff",
                    border: "1.5px solid #e0e7ff", borderRadius: 10, padding: "10px 14px",
                    fontWeight: 700, fontSize: "0.84rem", color: "#4f46e5",
                    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                  }}
                >
                  <i className="bi bi-person-plus" /> Manuel Ekle
                </button>
              </div>
            )}

            {/* Ekle formu */}
            {showAdd && (
              <div style={{ background: "#f8faff", border: "1.5px solid #e0e7ff", borderRadius: 14, padding: "14px" }}>
                <div style={{ fontWeight: 700, fontSize: "0.82rem", color: "#4338ca", marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span><i className="bi bi-person-plus me-2" />Yeni Nöbetçi</span>
                  <button onClick={() => { setShowAdd(false); setForm(EMPTY_FORM); setAddError(null); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: "1rem" }}>
                    <i className="bi bi-x" />
                  </button>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {/* Kullanıcı seçimi */}
                  <div>
                    <label style={{ fontSize: "0.7rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 4 }}>
                      Kullanıcı *
                    </label>
                    <div style={{ display: "flex", gap: 6 }}>
                      <input
                        type="text"
                        readOnly
                        value={form.userId ? `ID: ${form.userId.slice(0, 8)}…` : ""}
                        placeholder="Kullanıcı seçilmedi"
                        style={{ flex: 1, borderRadius: 9, border: "1.5px solid #e0e7ff", padding: "8px 12px", fontSize: "0.82rem", color: form.userId ? "#111827" : "#9ca3af", background: "#fff", outline: "none", fontFamily: "inherit" }}
                      />
                      <button
                        onClick={() => setShowPicker(true)}
                        style={{ background: "#eef2ff", border: "1.5px solid #c7d2fe", borderRadius: 9, padding: "8px 12px", fontWeight: 600, fontSize: "0.8rem", color: "#4f46e5", cursor: "pointer", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 5 }}
                      >
                        <i className="bi bi-people" /> Listeden Seç
                      </button>
                    </div>
                  </div>

                  {/* Ad Soyad */}
                  <div>
                    <label style={{ fontSize: "0.7rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 4 }}>
                      Görünen Ad *
                    </label>
                    <input
                      type="text"
                      value={form.displayName}
                      onChange={(e) => setForm((p) => ({ ...p, displayName: e.target.value }))}
                      placeholder="Ad Soyad"
                      style={{ width: "100%", borderRadius: 9, border: "1.5px solid #e0e7ff", padding: "8px 12px", fontSize: "0.88rem", color: "#111827", background: "#fff", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
                    />
                  </div>

                  {/* Telefon */}
                  <div>
                    <label style={{ fontSize: "0.7rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 4 }}>
                      Telefon
                    </label>
                    <input
                      type="tel"
                      value={form.phoneNumber}
                      onChange={(e) => setForm((p) => ({ ...p, phoneNumber: e.target.value }))}
                      placeholder="5XXXXXXXXX"
                      style={{ width: "100%", borderRadius: 9, border: "1.5px solid #e0e7ff", padding: "8px 12px", fontSize: "0.88rem", color: "#111827", background: "#fff", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
                    />
                  </div>

                  {/* Aktif toggle */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Toggle on={form.isActive} loading={false} onChange={() => setForm((p) => ({ ...p, isActive: !p.isActive }))} />
                    <span style={{ fontSize: "0.82rem", fontWeight: 600, color: form.isActive ? "#16a34a" : "#6b7280" }}>
                      {form.isActive ? "Aktif olarak ekle" : "Pasif olarak ekle"}
                    </span>
                  </div>

                  {/* Hata */}
                  {addError && (
                    <div style={{ padding: "8px 12px", borderRadius: 8, background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", fontSize: "0.78rem" }}>
                      {addError}
                    </div>
                  )}

                  {/* Ekle butonu */}
                  <button
                    onClick={handleAddSubmit}
                    disabled={addLoading || !form.displayName.trim() || !form.userId.trim()}
                    style={{
                      background: form.displayName.trim() && form.userId.trim() && !addLoading
                        ? "linear-gradient(135deg, #065f46, #047857)"
                        : "#e5e7eb",
                      border: "none", borderRadius: 10, padding: "10px 0",
                      fontWeight: 700, fontSize: "0.88rem",
                      color: form.displayName.trim() && form.userId.trim() && !addLoading ? "#fff" : "#9ca3af",
                      cursor: form.displayName.trim() && form.userId.trim() && !addLoading ? "pointer" : "default",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                      transition: "all 0.15s",
                    }}
                  >
                    {addLoading
                      ? <><span className="spinner-border spinner-border-sm" /> Ekleniyor…</>
                      : <><i className="bi bi-check-lg" /> Nöbetçi Ekle</>
                    }
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: isNarrow ? "12px 16px" : "12px 20px",
          borderTop: "1.5px solid #f1f5f9", background: "#fafafa",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexShrink: 0,
        }}>
          <button
            onClick={loadAgents}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", fontSize: "0.8rem", display: "flex", alignItems: "center", gap: 5, fontWeight: 600 }}
          >
            <i className="bi bi-arrow-clockwise" /> Yenile
          </button>
          <button
            onClick={onClose}
            style={{
              background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: 10,
              padding: isNarrow ? "10px 24px" : "7px 22px",
              fontWeight: 700, fontSize: "0.88rem", color: "#374151",
              cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
            }}
          >
            <i className="bi bi-x-lg" /> Kapat
          </button>
        </div>
      </div>
    </>
  );
}
